import type { CSSProperties } from "react";
import type { LucideIcon } from "lucide-react";
import {
  AlertTriangle,
  Armchair,
  Bath,
  BedDouble,
  Bird,
  Bone,
  Castle,
  Cat,
  Crown,
  Dog,
  Fence,
  Fish,
  Flower2,
  Gem,
  Heart,
  Home,
  Leaf,
  Lock,
  Moon,
  PawPrint,
  Rabbit,
  Shield,
  Sofa,
  Sparkles,
  Squirrel,
  Star,
  Stethoscope,
  Sun,
  Tent,
  Trees,
  Turtle,
  Warehouse,
  Waves,
} from "lucide-react";

/** Een accommodatietype dat het pension zelf aanmaakt, per diersoort, uit GET /api/pension/accommodatietypes. */
export interface AccommodatieType {
  id: number;
  diersoortId: number;
  diersoort: string | null;
  naam: string;
  icoon: string | null;
  /** Een eigen logo als data-URL; gaat voor het icoon. */
  afbeelding: string | null;
  kleur: string;
  luxe: boolean;
  quarantaine: boolean;
  positie: number;
  /** Hoe dit type in de planning telt en wat er bij het plaatsen meeweegt. */
  planning?: {
    capaciteitsmodel: string;
    maxDieren: number | null;
    minDieren: number | null;
    bufferMinuten: number | null;
    delenTussenReserveringen: boolean;
    alleenZelfdeEigenaar: boolean;
    voorkeurAutomatisch: boolean;
    actief: boolean;
    schoonmaakblok: boolean;
    regelGeslacht: boolean;
    regelCastratie: boolean;
    regelFormaat: boolean;
    regelLeeftijd: boolean;
    regelSociaal: boolean;
    regelGoedkeuring: boolean;
    notities: string | null;
  } | null;
  /** De geldende prijs per dag uit de pensiontarieven; leeg zonder tarief. */
  prijs: { bedrag: number; bedragHoog: number | null; geldigVanaf: string | null } | null;
  /** Wat het verblijf is, voor de wizard Nieuwe reservering. */
  omschrijving?: string | null;
  /** Een foto van het verblijf als data-URL. */
  foto?: string | null;
}

/** Pensionprijzen: het tarief van het dier, met de prijs van het type als toeslag. Accommodatie: de dagprijs van het type. */
export type Prijsberekening = "PENSIONPRIJZEN" | "ACCOMMODATIE";

/** De iconen waaruit het pension kiest; de sleutel is wat de api bewaart. */
export const ICONEN: Record<string, LucideIcon> = {
  home: Home,
  crown: Crown,
  gem: Gem,
  star: Star,
  sparkles: Sparkles,
  "bed-double": BedDouble,
  sofa: Sofa,
  armchair: Armchair,
  castle: Castle,
  warehouse: Warehouse,
  tent: Tent,
  fence: Fence,
  trees: Trees,
  leaf: Leaf,
  "flower-2": Flower2,
  sun: Sun,
  moon: Moon,
  waves: Waves,
  bath: Bath,
  heart: Heart,
  shield: Shield,
  lock: Lock,
  stethoscope: Stethoscope,
  "alert-triangle": AlertTriangle,
  dog: Dog,
  cat: Cat,
  rabbit: Rabbit,
  squirrel: Squirrel,
  bird: Bird,
  fish: Fish,
  turtle: Turtle,
  bone: Bone,
  "paw-print": PawPrint,
};

/** Het logo of icoon van een accommodatietype, in zijn eigen kleur. Zonder type een pootje. */
export function AccommodatieIcoon({
  type,
  size = 16,
}: {
  type: Pick<AccommodatieType, "icoon" | "afbeelding" | "kleur"> | null | undefined;
  size?: number;
}) {
  if (type?.afbeelding) {
    return <img src={type.afbeelding} alt="" className="kp-logo" style={{ width: size, height: size } as CSSProperties} />;
  }
  const Icoon = (type?.icoon ? ICONEN[type.icoon] : undefined) ?? PawPrint;
  return <Icoon size={size} aria-hidden="true" style={{ color: type?.kleur ?? undefined, flex: "none" }} />;
}

const SOORT_ICONEN: [RegExp, LucideIcon][] = [
  [/hond/i, Dog],
  [/kat/i, Cat],
  [/konijn/i, Rabbit],
  [/knaag|cavia|hamster|rat|muis/i, Squirrel],
  [/vogel/i, Bird],
  [/vis/i, Fish],
  [/schildpad|reptiel/i, Turtle],
];

export function DiersoortIcoon({ naam, size = 16 }: { naam: string | null | undefined; size?: number }) {
  const Icoon = SOORT_ICONEN.find(([patroon]) => patroon.test(naam ?? ""))?.[1] ?? PawPrint;
  return <Icoon size={size} aria-hidden="true" />;
}

/** Het meervoud van een diersoort voor een kop of filter: "Hond" wordt "Honden", "Kat" wordt "Katten". */
export function meervoud(naam: string | null | undefined): string {
  if (!naam) return "—";
  const woord = naam.trim();
  if (/kat$/i.test(woord)) return `${woord}ten`;
  if (/(dier|konijn|hond|vogel)$/i.test(woord)) return `${woord}en`;
  return woord;
}
